/**
 * Lê a expressão de prazo que o operador escreveu no campo de registro inline
 * (spec `2026-08-14-aba-acoes-conversa-em-curso-design.md` §5.4).
 *
 * **Mora em `@lume/shared` desde 15/Ago/2026** (fatia M3 do mobile): o `+ registrar`
 * inline existe agora nas duas telas, e o vocabulário de prazo que uma entende tem de
 * ser o que a outra entende. Um parser divergente é pior que parser nenhum — o operador
 * aprende «ate sexta» no desktop e descobre no celular que ali não vale.
 *
 * Devolve os ÍNDICES além da data: o campo grifa a expressão na própria frase e mostra
 * o valor resolvido ao lado ANTES de gravar. **Nunca se grava data que o operador não
 * viu** — é a trava que separa «prazo que cobra» de «data que apareceu do nada».
 *
 * **Deliberadamente conservador.** Sem expressão reconhecida devolve `null`, e o
 * registro nasce sem prazo. Chutar é pior que não ter: um prazo errado cobra na hora
 * errada, e bastam duas ou três dessas para o operador parar de confiar no registro
 * inteiro — o mesmo raciocínio da trava «na dúvida o motor não cria».
 *
 * Trabalho DIFERENTE do `computeCommitmentDeadline` do engine: aquele INFERE um prazo
 * de uma frase de promessa que ninguém escreveu para ser lida por máquina; este LÊ uma
 * expressão que o operador digitou de propósito, e precisa devolver onde ela está.
 */
/** Fim de expediente — usado quando a expressão diz o dia mas não a hora. */
const END_OF_DAY_HOUR = 18;
/** Dias da semana em pt-BR. Ordem importa: as formas mais longas vêm antes. */
const WEEKDAYS = [
    ['segunda-feira', 1],
    ['terça-feira', 2],
    ['quarta-feira', 3],
    ['quinta-feira', 4],
    ['sexta-feira', 5],
    ['domingo', 0],
    ['segunda', 1],
    ['terça', 2],
    ['terca', 2],
    ['quarta', 3],
    ['quinta', 4],
    ['sexta', 5],
    ['sábado', 6],
    ['sabado', 6],
];
/**
 * Fronteira de palavra ciente de acento. `\b` do JavaScript é ASCII: em `amanhã` a
 * fronteira final cai DEPOIS de um caractere que ele não considera «palavra», e o
 * casamento falha. Mesma armadilha em `terça` e `sábado` — metade do vocabulário de
 * prazo em português. Lookarounds sobre `\p{L}` resolvem, e o flag `u` é obrigatório.
 */
function wordRe(word) {
    return new RegExp(`(?<![\\p{L}\\p{N}])${word}(?![\\p{L}\\p{N}])`, 'u');
}
function atHour(base, hour, minute) {
    const d = new Date(base);
    d.setHours(hour, minute, 0, 0);
    return d;
}
/** Hora explícita que acompanha a expressão: «14h», «14:30», «às 9h», «9h30». */
function readHour(lower, from) {
    const m = /^\s*(?:às?\s*)?(\d{1,2})(?::(\d{2})|h(\d{2})?)/.exec(lower.slice(from));
    if (m === null)
        return null;
    const hour = Number(m[1]);
    const minute = Number(m[2] ?? m[3] ?? 0);
    // Hora impossível é ruído, não intenção — cai no fim de expediente.
    if (hour > 23 || minute > 59)
        return null;
    return { hour, minute };
}
export function parseDeadlinePhrase(text, now) {
    const lower = text.toLowerCase();
    const build = (start, end, day) => {
        const h = readHour(lower, end);
        const at = h === null ? atHour(day, END_OF_DAY_HOUR, 0) : atHour(day, h.hour, h.minute);
        return { start, end, phrase: text.slice(start, end), at };
    };
    // 1. «hoje» / «amanhã»
    for (const [word, offset] of [
        ['hoje', 0],
        ['amanhã', 1],
        ['amanha', 1],
    ]) {
        const m = wordRe(word).exec(lower);
        if (m !== null) {
            const day = new Date(now);
            day.setDate(day.getDate() + offset);
            return build(m.index, m.index + word.length, day);
        }
    }
    // 2. «em N dias» / «em N semanas»
    const rel = /(?<![\p{L}\p{N}])em\s+(\d{1,2})\s+(dias?|semanas?)(?![\p{L}\p{N}])/u.exec(lower);
    if (rel !== null) {
        const n = Number(rel[1] ?? '0');
        const unit = rel[2] ?? '';
        const day = new Date(now);
        day.setDate(day.getDate() + (unit.startsWith('semana') ? n * 7 : n));
        return build(rel.index, rel.index + rel[0].length, day);
    }
    // 3. Dia da semana — SEMPRE a próxima ocorrência. Nunca hoje: um prazo que nasce
    //    hoje-mais-cedo já nasce vencido, e a fronteira Unicode impede casar dentro de outra palavra
    //    («sextante» não é sexta).
    for (const [word, target] of WEEKDAYS) {
        const m = wordRe(word).exec(lower);
        if (m !== null) {
            const day = new Date(now);
            // `|| 7` fazia este salto quando o dia pedido é HOJE. Virou checagem explícita ao
            // mudar de casa: a regra do `@lume/shared` proíbe número em condicional, e aqui
            // ela tem razão — o zero é justamente o caso que a linha existe para tratar, e
            // escondê-lo atrás de um falsy é o que faz alguém «simplificar» e quebrar.
            const raw = (target - day.getDay() + 7) % 7;
            day.setDate(day.getDate() + (raw === 0 ? 7 : raw));
            return build(m.index, m.index + word.length, day);
        }
    }
    return null;
}
//# sourceMappingURL=deadline-phrase.js.map